import { useState } from 'react'
import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Settings, Users, Package, Calendar, Save, Eye } from 'lucide-react'
import { toast } from 'sonner'

interface Props {
  settings: {
    crm_inactive_days: number
    crm_remaining_lessons_alert: number
    crm_expiry_days_alert: number
    crm_acquisition_expiry_days: number
    crm_inactive_enabled: boolean
    crm_remaining_lessons_enabled: boolean
    crm_expiry_enabled: boolean
  }
  preview: {
    inactive: number
    low_lessons: number
    expiring: number
    total: number
  }
}

export default function CrmSettings({ settings, preview }: Props) {
  const [form, setForm] = useState(settings)

  const hasChanges =
    form.crm_inactive_days !== settings.crm_inactive_days ||
    form.crm_remaining_lessons_alert !== settings.crm_remaining_lessons_alert ||
    form.crm_expiry_days_alert !== settings.crm_expiry_days_alert ||
    form.crm_acquisition_expiry_days !== settings.crm_acquisition_expiry_days ||
    form.crm_inactive_enabled !== settings.crm_inactive_enabled ||
    form.crm_remaining_lessons_enabled !== settings.crm_remaining_lessons_enabled ||
    form.crm_expiry_enabled !== settings.crm_expiry_enabled

  const handleSave = () => {
    router.patch('/crm/settings', { school: form }, {
      onSuccess: () => toast.success('Configurações salvas!'),
      onError: () => toast.error('Erro ao salvar configurações.')
    })
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => router.visit('/crm')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                <Settings className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Configurações do CRM</h1>
                <p className="text-sm text-muted-foreground">Gatilhos de retenção e limites</p>
              </div>
            </div>
          </div>
          <Button onClick={handleSave} disabled={!hasChanges}>
            <Save className="h-4 w-4 mr-1" />
            Salvar
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Config Cards */}
          <div className="lg:col-span-2 space-y-4">
            {/* Aluno Sumido */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-red-500" />
                    <div>
                      <CardTitle className="text-base">Aluno Sumido</CardTitle>
                      <CardDescription>Detecta alunos sem presença por período</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={form.crm_inactive_enabled}
                    onCheckedChange={(v) => setForm({ ...form, crm_inactive_enabled: v })}
                  />
                </div>
              </CardHeader>
              {form.crm_inactive_enabled && (
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Label className="whitespace-nowrap">Considerar sumido após</Label>
                    <Input
                      type="number"
                      min={1}
                      value={form.crm_inactive_days}
                      onChange={(e) => setForm({ ...form, crm_inactive_days: parseInt(e.target.value) || 0 })}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">dias sem presença</span>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Pacote Esgotando */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-orange-500" />
                    <div>
                      <CardTitle className="text-base">Pacote Esgotando</CardTitle>
                      <CardDescription>Alerta quando restam poucas aulas</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={form.crm_remaining_lessons_enabled}
                    onCheckedChange={(v) => setForm({ ...form, crm_remaining_lessons_enabled: v })}
                  />
                </div>
              </CardHeader>
              {form.crm_remaining_lessons_enabled && (
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Label className="whitespace-nowrap">Alertar quando restam</Label>
                    <Input
                      type="number"
                      min={1}
                      value={form.crm_remaining_lessons_alert}
                      onChange={(e) => setForm({ ...form, crm_remaining_lessons_alert: parseInt(e.target.value) || 0 })}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">aulas ou menos</span>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Assinatura Vencendo */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-yellow-500" />
                    <div>
                      <CardTitle className="text-base">Assinatura Vencendo</CardTitle>
                      <CardDescription>Alerta antes do vencimento</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={form.crm_expiry_enabled}
                    onCheckedChange={(v) => setForm({ ...form, crm_expiry_enabled: v })}
                  />
                </div>
              </CardHeader>
              {form.crm_expiry_enabled && (
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Label className="whitespace-nowrap">Alertar quando faltam</Label>
                    <Input
                      type="number"
                      min={1}
                      value={form.crm_expiry_days_alert}
                      onChange={(e) => setForm({ ...form, crm_expiry_days_alert: parseInt(e.target.value) || 0 })}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">dias para vencer</span>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Aquisição Expiry */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Expiração de Leads de Aquisição
                </CardTitle>
                <CardDescription>
                  Leads de aquisição sem interação após este período podem ser marcados como perdidos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Label className="whitespace-nowrap">Expirar após</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.crm_acquisition_expiry_days}
                    onChange={(e) => setForm({ ...form, crm_acquisition_expiry_days: parseInt(e.target.value) || 0 })}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">dias sem interação</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Preview
                </CardTitle>
                <CardDescription>Alunos que seriam leads de retenção hoje</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-primary">{preview.total}</p>
                  <p className="text-sm text-muted-foreground">alunos em risco</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-red-500" />
                      <span className="text-sm">Sumidos</span>
                    </div>
                    <Badge variant="destructive">{preview.inactive}</Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-orange-500" />
                      <span className="text-sm">Pacote esgotando</span>
                    </div>
                    <Badge variant="secondary">{preview.low_lessons}</Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm">Assinatura vencendo</span>
                    </div>
                    <Badge variant="outline">{preview.expiring}</Badge>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground pt-2">
                  O job de retenção roda diariamente às 7h e cria leads automaticamente para esses alunos.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
